import { store, type ProfanityLevel } from "../state";
import { PROFANITY_STEPS } from "../copy";
import "./dial.css";
import { haptic } from "./haptics";

/**
 * The rudeness dial as a segmented control, shared by every look: a thumb
 * slides to the picked level, and you can drag along the row to scrub
 * through levels (a haptic tick on each). How the thumb and the rest of the
 * screen react is up to each look — views get onChange with the level, the
 * previous one and the cell that was picked.
 *
 * Markup: row > .dial-thumb + button.dial-cell[data-level] > span.dial-label.
 * The row carries data-level so CSS can colour things by level.
 */
export function dialCellsHTML(cellClass: string): string {
  return (
    `<span class="dial-thumb" aria-hidden="true"></span>` +
    PROFANITY_STEPS.map(
      (s) =>
        `<button type="button" class="dial-cell ${cellClass}" data-level="${s.level}" role="radio" aria-checked="false"><span class="dial-label">${s.label}</span></button>`
    ).join("")
  );
}

export interface Dial {
  /** Move the thumb and state to `level`. Called from the store subscription. */
  sync(level: ProfanityLevel): void;
  destroy(): void;
}

export function mountDial(
  row: HTMLElement,
  onChange: (level: ProfanityLevel, prev: ProfanityLevel, cell: HTMLElement) => void
): Dial {
  const cells = Array.from(row.querySelectorAll<HTMLElement>(".dial-cell"));
  row.setAttribute("role", "radiogroup");
  row.setAttribute("aria-label", "How rude");
  let current: ProfanityLevel | -1 = -1;

  const pick = (level: ProfanityLevel) => {
    if (level === store.get().profanityLevel) return;
    haptic();
    store.setProfanityLevel(level);
  };

  // Tap: the click lands on the cell. Drag: scrub along the row.
  let dragging = false;
  let startX = 0;
  let pointerId = -1;
  let suppressClick = false;

  const levelAt = (clientX: number): ProfanityLevel => {
    const r = row.getBoundingClientRect();
    const i = Math.floor(((clientX - r.left) / r.width) * cells.length);
    return Math.max(0, Math.min(cells.length - 1, i)) as ProfanityLevel;
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    pointerId = e.pointerId;
    startX = e.clientX;
    dragging = false;
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    if (!dragging && Math.abs(e.clientX - startX) > 8) {
      dragging = true;
      row.classList.add("is-dragging");
      try {
        row.setPointerCapture(e.pointerId);
      } catch {
        // capture is a nicety; scrubbing still works while over the row
      }
    }
    if (dragging) pick(levelAt(e.clientX));
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    pointerId = -1;
    if (dragging) {
      suppressClick = true;
      window.setTimeout(() => (suppressClick = false), 0);
    }
    dragging = false;
    row.classList.remove("is-dragging");
  };
  const onClick = (e: MouseEvent) => {
    if (suppressClick) return;
    const cell = (e.target as HTMLElement).closest<HTMLElement>(".dial-cell");
    if (cell) pick(Number(cell.dataset.level) as ProfanityLevel);
  };

  row.addEventListener("pointerdown", onDown);
  row.addEventListener("pointermove", onMove);
  row.addEventListener("pointerup", onUp);
  row.addEventListener("pointercancel", onUp);
  row.addEventListener("click", onClick);

  function sync(level: ProfanityLevel) {
    if (level === current) return;
    const prev = current;
    current = level;
    row.style.setProperty("--i", String(level));
    row.dataset.level = String(level);
    cells.forEach((c, i) => {
      c.classList.toggle("active", i === level);
      c.setAttribute("aria-checked", String(i === level));
    });
    if (prev !== -1) onChange(level, prev, cells[level]);
  }

  sync(store.get().profanityLevel);

  return {
    sync,
    destroy() {
      row.removeEventListener("pointerdown", onDown);
      row.removeEventListener("pointermove", onMove);
      row.removeEventListener("pointerup", onUp);
      row.removeEventListener("pointercancel", onUp);
      row.removeEventListener("click", onClick);
    },
  };
}
